/**
 * PageTitleBar — the ONE fused bar at the top of a page node (web render).
 *
 * The device frame IS the page's titlebar. It shows the site's ADDRESS
 * (display-only) and the ⋯ menu — the editable page NAME lives outside the node
 * as a Figma-style label (PageNameLabel), so this bar is pure device chrome.
 * Layout is constant across devices — address · ⋯ — only the flavour changes:
 *   • desktop → a macOS browser bar (traffic lights + live dot + URL pill)
 *   • tablet  → an iPad Safari bar (centered pill, no lights)
 *   • mobile  → the iOS status bar (time · signal/battery · ⋯) — no URL, the
 *     name label carries identity on the narrow frame.
 * Home shows the bare domain (its real path is `/`); other pages show `/slug`.
 *
 * This is (one of) the xyflow drag handles (`.wb-page-drag`); the whole bar
 * drags like a window titlebar. RSD's html.* takes no className, so the hook
 * class is put on the element through a ref (usePageDragHandle).
 * react-strict-dom (html.* + css.create).
 */
import { useLayoutEffect, useRef } from "react";
import { css, html } from "react-strict-dom";
import { Icon, Tooltip } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";

import type { Breakpoint } from "./breakpoints.js";

/** The xyflow drag-handle selector SitemapCanvas gives every page node. */
export const PAGE_DRAG_HANDLE_CLASS = "wb-page-drag";

/**
 * Puts the xyflow drag-handle class on an html.* element (which takes no
 * className). Re-applied after every commit: React rewrites `className` when
 * the element's StyleX classes change, which would drop a class added once.
 */
export function usePageDragHandle<T extends HTMLElement>(enabled = true) {
  const ref = useRef<T | null>(null);
  useLayoutEffect(() => {
    ref.current?.classList.toggle(PAGE_DRAG_HANDLE_CLASS, enabled);
  });
  return ref;
}

/** A page label → a display path segment, e.g. "About Us" → "/about-us". */
function slugPath(label: string): string {
  const s = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return "/" + (s || "page");
}

const DOMAIN = "yoursite.com";

interface PageTitleBarProps {
  breakpoint: Breakpoint;
  label: string;
  /** URL-slug override (⋯ "Change URL…"); absent → derived from the label. */
  slugOverride?: string;
  readOnly: boolean;
  /** The home page's real path is `/` — show the bare domain, no slug. */
  isHome: boolean;
  /** The ⋯ page-actions button (built in PageNode with its ref + handler). */
  menuSlot: React.ReactNode;
  /** URL-edit pencil (built in PageNode); shown next to the pill on the selected
   *  non-home page. Absent → no pencil (home, unselected, mobile, read-only). */
  urlEditSlot?: React.ReactNode;
}

const s = css.create({
  bar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    height: sizes.controlLg,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    boxSizing: "border-box",
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    backgroundColor: colors.bgCard,
    fontSize: typeScale.fontSize2,
    color: colors.textMuted,
    flexShrink: 0,
    cursor: "grab",
    userSelect: "none",
  },
  barMobile: {
    height: sizes.toolbarH,
    backgroundColor: colors.bg,
  },
  barReadOnly: {
    cursor: "default",
  },
  clock: {
    fontWeight: typography.fontWeightBold,
    color: colors.text,
    fontSize: typography.fontSizeXs,
    fontVariantNumeric: "tabular-nums",
  },
  statusIcons: {
    marginLeft: "auto",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: microSpacing.px5,
    color: colors.text,
  },
  urlPill: {
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    minWidth: 0,
    maxWidth: "100%",
    height: sizes.chip,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    borderRadius: radii.full,
    backgroundColor: colors.bgCardHover,
    color: colors.textMuted,
    fontSize: typeScale.fontSize2,
  },
  urlPillDesktop: {
    borderRadius: radii.sm,
  },
  publishedDot: {
    display: "block",
    width: sizes.indicatorDot,
    height: sizes.indicatorDot,
    borderRadius: radii.full,
    backgroundColor: colors.success,
    // The halo ring around the live dot.
    outlineWidth: borders.accent,
    outlineStyle: "solid",
    outlineColor: colors.successBorder,
    flexShrink: 0,
  },
  lock: {
    opacity: 0.55,
    fontSize: typography.fontSizeXs,
  },
  address: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  path: {
    color: colors.text,
    fontWeight: typography.fontWeightSemibold,
  },
  lights: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    flexShrink: 0,
  },
  trafficDot: {
    width: sizes.indicatorDot,
    height: sizes.indicatorDot,
    borderRadius: radii.full,
  },
  lightClose: { backgroundColor: colors.error },
  lightMinimize: { backgroundColor: colors.warning },
  lightZoom: { backgroundColor: colors.success },
  menuSpacer: {
    width: sizes.controlSm,
    flexShrink: 0,
  },
  track: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "0%",
    minWidth: 0,
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: spacing.xs,
  },
});

export function PageTitleBar({
  breakpoint,
  label,
  slugOverride,
  readOnly,
  isHome,
  menuSlot,
  urlEditSlot,
}: PageTitleBarProps) {
  const mobile = breakpoint === "mobile";
  const desktop = breakpoint === "desktop";
  const dragHandleRef = usePageDragHandle<HTMLDivElement>();
  // An override wins over the label-derived path; home shows the bare domain
  // only when it has NO override (its real path is `/`).
  const pathSeg = slugOverride ? slugPath(slugOverride) : slugPath(label);
  const showPath = !!slugOverride || !isHome;

  const bar = [s.bar, mobile && s.barMobile, readOnly && s.barReadOnly];

  if (mobile) {
    // iOS status bar — pure device cue; identity comes from the name label.
    return (
      <html.div ref={dragHandleRef} style={bar}>
        <html.span style={s.clock}>9:41</html.span>
        <html.span aria-hidden style={s.statusIcons}>
          <Icon name="signal" size="sm" />
          <Icon name="battery" size="sm" />
        </html.span>
        {menuSlot}
      </html.div>
    );
  }

  // The site address as a display-only URL pill.
  const urlPill = (
    <html.span style={[s.urlPill, desktop && s.urlPillDesktop]}>
      {desktop && (
        <Tooltip.Root content="Published">
          <Tooltip.Trigger>
            <html.span aria-hidden style={s.publishedDot} />
          </Tooltip.Trigger>
        </Tooltip.Root>
      )}
      <html.span aria-hidden style={s.lock}>
        🔒
      </html.span>
      <html.span style={s.address}>
        {DOMAIN}
        {showPath && <html.span style={s.path}>{pathSeg}</html.span>}
      </html.span>
    </html.span>
  );

  // Desktop / tablet: a browser titlebar.
  return (
    <html.div ref={dragHandleRef} style={bar}>
      {desktop ? (
        <html.span aria-hidden style={s.lights}>
          <html.span style={[s.trafficDot, s.lightClose]} />
          <html.span style={[s.trafficDot, s.lightMinimize]} />
          <html.span style={[s.trafficDot, s.lightZoom]} />
        </html.span>
      ) : (
        // Tablet: a menu-width spacer keeps the pill truly centered.
        <html.span aria-hidden style={s.menuSpacer} />
      )}
      {/* A flex:1 track between the lights and the ⋯ menu: it centers the pill +
          its edit pencil, and lets the pill size to its content — the URL shows
          in full whenever there's room, ellipsizing only when it truly runs out
          (not squeezed by a percentage max-width on an empty bar). */}
      <html.span style={s.track}>
        {urlPill}
        {urlEditSlot}
      </html.span>
      {menuSlot}
    </html.div>
  );
}
